// src/components/shared/CameraScannerPage.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useScanner } from "../../utils/ScannerContext";

const HAS_DETECTOR = typeof window !== "undefined" && "BarcodeDetector" in window;

export default function CameraScannerPage() {
  const navigate = useNavigate();
  const { complete, cancel, takePrestartedStream, pending } = useScanner();

  const videoRef = useRef(null);
  const rafRef = useRef(0);
  const detectorRef = useRef(null);

  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");
  const [justScanned, setJustScanned] = useState(false);

  const cleanup = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = 0;
    const v = videoRef.current;
    try { v?.srcObject?.getTracks?.().forEach(t => t.stop()); } catch {}
    if (v) v.srcObject = null;
  }, []);

  const startDetectLoop = useCallback(() => {
    detectorRef.current = new window.BarcodeDetector({
      formats: ["qr_code","ean_13","ean_8","code_128","code_39","itf","upc_a","upc_e","pdf417","data_matrix","aztec"],
    });
    const tick = async () => {
      const v = videoRef.current;
      if (!v || v.readyState < 2) { rafRef.current = requestAnimationFrame(tick); return; }
      try {
        const codes = await detectorRef.current.detect(v);
        if (codes?.length) {
          const raw = codes[0].rawValue || codes[0].formattedValue || "";
          if (raw) {
            try { navigator.vibrate?.(30); } catch {}
            setJustScanned(true);
            setTimeout(() => {
              complete(raw);   // resolve the opener's promise
              cleanup();
              navigate(-1);    // auto go back to previous screen
            }, 180);
            return;
          }
        }
      } catch {}
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [cleanup, complete, navigate]);

  useEffect(() => {
    setError(""); setStarting(true);
    (async () => {
      try {
        if (!HAS_DETECTOR) throw new Error("Browser lacks BarcodeDetector");
        let stream = takePrestartedStream();
        if (!stream) {
          // desktop can start here (mobile would require an extra tap if you get here without preStartStream)
          stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } },
            audio: false,
          });
        }
        const v = videoRef.current;
        v.muted = true; v.playsInline = true; v.autoplay = true;
        v.srcObject = stream;
        await v.play();
        startDetectLoop();
      } catch (e) {
        const name = e?.name || "Error";
        const msg = e?.message || String(e);
        setError(`(${name}) ${msg}`);
      } finally {
        setStarting(false);
      }
    })();

    return () => cleanup();
  }, [startDetectLoop, cleanup, takePrestartedStream]);

  return (
    <div className="position-fixed top-0 start-0 w-100 h-100 bg-dark text-white" style={{ zIndex: 3000 }}>
      <div className="d-flex align-items-center justify-content-between p-2">
        <button className="btn btn-light btn-sm" onClick={() => { cleanup(); cancel(); navigate(-1); }}>
          Κλείσιμο
        </button>
        <div className="fw-bold">{pending?.title || "Σάρωση"}</div>
        <div style={{ width: 60 }} />
      </div>

      <div className="w-100 h-100 position-relative">
        <video ref={videoRef} className="w-100 h-100" style={{ objectFit: "cover", background: "#000" }} />
        <div className="position-absolute top-50 start-50 translate-middle"
             style={{ width: 260, height: 260, border: "2px solid #fff", borderRadius: 12 }} />
        {starting && (
          <div className="position-absolute top-50 start-50 translate-middle text-center">
            <div className="spinner-border text-light" role="status" />
            <div className="mt-2">Άνοιγμα κάμερας…</div>
          </div>
        )}
        {error && (
          <div className="position-absolute bottom-0 start-50 translate-middle-x mb-4">
            <div className="alert alert-warning m-0">{error}</div>
          </div>
        )}
        {justScanned && (
          <div style={{
            position: "absolute", inset: 0,
            background: "rgba(0,200,0,0.22)", border: "3px solid rgba(0,200,0,0.9)",
            animation: "scanFlashFade 240ms ease-out forwards", pointerEvents: "none"
          }}/>
        )}
        <style>{`@keyframes scanFlashFade { 0%{opacity:1;} 100%{opacity:0;} }`}</style>
      </div>
    </div>
  );
}
